import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { Progress } from "@/components/ui/progress"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { ArrowRight } from "lucide-react"
import { Link } from "react-router"
import { ORG_REGULATIONS_SETTINGS_ROUTE } from "../../../api/regulationSettingsApi"
import type { DashboardFrameworkRow } from "../../../api/types"
import { useCanViewOrgRegulations } from "../../../hooks/useCanViewOrgRegulations"
import {
  normalizeFrameworkCode,
  regulationDescription,
  regulationLabel,
  regulationShortLabel,
} from "../../../utils/regulationLabel"
import { DualValue } from "./DualValue"
import { formatInt, localeForLanguage } from "./dashboardStore"

export interface FrameworksBoardProps {
  frameworks: DashboardFrameworkRow[]
  /** The dashboard did not load: no rows means "unknown", so the "none selected"
   *  hint stays hidden (the board's error banner says what happened). */
  loadFailed: boolean
  /** Popover row label (e.g. "Suppliers" / "Products"). */
  popLabelKey: TranslationKeys
  /** What "covered" counts on this dashboard, shown under the popover counts. */
  coveredHintKey: TranslationKeys
  /** # mode shows "count / total" instead of the bare count — see
   * DualValue's showTotalInAbs doc. */
  showTotalInAbs?: boolean
}

/**
 * Per-regulation coverage bars shared by the supplier and customer Studio
 * dashboards, one row per regulation in the org's set (Settings › Regelwerke),
 * differing only in the popover label and whether # mode shows the total.
 * Rows are keyed by canonical framework key and labelled here: the short form
 * in the row, the full name in the hover card.
 */
export function FrameworksBoard({
  frameworks,
  loadFailed,
  popLabelKey,
  coveredHintKey,
  showTotalInAbs,
}: FrameworksBoardProps) {
  const { translate, currentLanguage } = useTranslation()
  const canOpenRegulations = useCanViewOrgRegulations()
  const locale = localeForLanguage(currentLanguage)

  if (frameworks.length === 0) {
    if (loadFailed) return null
    return (
      <div className="py-3">
        <p className="text-sm text-grey5">
          {translate("MC_DASH_NO_REGULATIONS")}
        </p>
        {canOpenRegulations && (
          <Link
            to={ORG_REGULATIONS_SETTINGS_ROUTE}
            className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-yellow1 hover:underline"
          >
            {translate("MC_CONF_V2_OPEN_REGULATIONS")}
            <ArrowRight size={11} />
          </Link>
        )}
      </div>
    )
  }

  return (
    <div>
      {frameworks.map((fw) => {
        const code = normalizeFrameworkCode(fw.key)
        // Every hover reads the same way: the full name, what it covers, the counts.
        const description = regulationDescription(code, translate)
        return (
          <HoverCard key={fw.key} openDelay={150}>
            <HoverCardTrigger asChild>
              <div
                tabIndex={0}
                className="grid grid-cols-[150px_1fr_auto] items-center gap-4 border-t border-white/5 py-3 first:border-t-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
              >
                <span className="text-sm text-white">
                  {regulationShortLabel(code, fw.label, translate)}
                </span>
                <Progress
                  value={
                    !fw.noData && fw.inScope
                      ? (fw.covered / fw.inScope) * 100
                      : 0
                  }
                  className="h-[5px] bg-white/10"
                />
                {fw.noData ? (
                  <span className="min-w-[60px] text-right text-[12px] text-grey5">
                    {translate("MC_DASH_NO_DATA_YET")}
                  </span>
                ) : (
                  <DualValue
                    count={fw.covered}
                    total={fw.inScope}
                    showTotalInAbs={showTotalInAbs}
                    className="min-w-[60px] text-right text-[13px] font-semibold text-white tabular-nums"
                  />
                )}
              </div>
            </HoverCardTrigger>
            <HoverCardContent
              align="start"
              className="w-56 border-grey4 bg-grey1"
            >
              <div className="mb-1.5">
                <p className="text-xs font-medium text-white">
                  {regulationLabel(code, fw.label, translate)}
                </p>
                {description && (
                  <p className="mt-0.5 text-xs text-grey5">{description}</p>
                )}
              </div>
              {fw.noData ? (
                <p className="text-[11px] text-grey5">
                  {translate("MC_DASH_NO_DATA_HINT")}
                </p>
              ) : (
                <>
                  <div className="flex items-baseline justify-between gap-4 text-sm">
                    <span className="text-xs text-grey5">
                      {translate(popLabelKey)}
                    </span>
                    <span className="font-semibold text-white tabular-nums">
                      {formatInt(fw.covered, locale)} /{" "}
                      {formatInt(fw.inScope, locale)}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[11px] text-grey5">
                    {translate(coveredHintKey)}
                  </p>
                </>
              )}
            </HoverCardContent>
          </HoverCard>
        )
      })}
    </div>
  )
}
